import { Table, TableData, TableHead, TableHeader } from '@squareup/dex-ui';
import React, { createContext, FC, PropsWithChildren, useContext } from 'react';

// Bridge MDX-rendered table elements onto the dex-ui Table family so they
// get the same border/padding/sticky-header styling as the markdown-source
// tables that render via remark-gfm. Bare <table>/<th>/<td> elements have
// no styling in the docs `.article` scope, so a multi-column table becomes
// visually unparseable (cells collapse together with no borders).
//
// Note: MDX/JSX uses `colSpan` / `rowSpan` (camelCase) while dex-ui's
// TableData/TableHeader expect `colspan` / `rowspan` (lowercase). The
// adapters translate so authors can keep using the standard JSX form.

interface HeaderCellProps {
  colSpan?: number | string;
  align?: 'left' | 'right' | 'center' | 'justify';
}

interface DataCellProps {
  colSpan?: number | string;
  rowSpan?: number | string;
  align?: 'left' | 'right' | 'center' | 'justify';
}

function toNumberOrUndefined(
  v: number | string | undefined
): number | undefined {
  if (v === null || v === undefined) {
    return undefined;
  }
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n : undefined;
}

// `<StickyTable>` in Fern wraps a markdown table; the inner table itself is
// what `MdxMdxTable` already renders as a dex-ui <Table>. We can't nest a
// <Table> inside a <Table>, so the wrapper instead flips a context flag
// that the inner table-adapter reads to enable `hasStickyHeader`.
const StickyTableContext = createContext(false);

const MdxMdxTable: FC<PropsWithChildren> = ({ children }) => {
  const isSticky = useContext(StickyTableContext);
  return (
    <Table hasStickyHeader={isSticky} margin={{ vertical: '5x' }}>
      {children}
    </Table>
  );
};

MdxMdxTable.displayName = 'MdxTable';

const MdxStickyTable: FC<PropsWithChildren> = ({ children }) => {
  return (
    <StickyTableContext.Provider value={true}>
      {children}
    </StickyTableContext.Provider>
  );
};

MdxStickyTable.displayName = 'MdxStickyTable';

const MdxMdxTableHead: FC<PropsWithChildren> = ({ children }) => {
  return <TableHead>{children}</TableHead>;
};

MdxMdxTableHead.displayName = 'MdxTableHead';

// dex-ui doesn't expose a TableRow component — bare <tr> works fine
// inside <Table> because the Table component's CSS targets
// `.table thead`, `.table td`, etc. without needing wrapper components.
const MdxMdxTableRow: FC<PropsWithChildren> = ({ children }) => {
  return <tr>{children}</tr>;
};

MdxMdxTableRow.displayName = 'MdxTableRow';

// dex-ui TableHeader has no rowspan prop, so we don't accept rowSpan here —
// the type stays narrow rather than silently dropping a prop authors might
// expect to work.
const MdxMdxTableHeader: FC<PropsWithChildren<HeaderCellProps>> = ({
  children,
  colSpan,
  align,
}) => {
  return (
    <TableHeader colspan={toNumberOrUndefined(colSpan)} align={align}>
      {children}
    </TableHeader>
  );
};

MdxMdxTableHeader.displayName = 'MdxTableHeader';

const MdxMdxTableData: FC<PropsWithChildren<DataCellProps>> = ({
  children,
  colSpan,
  rowSpan,
  align,
}) => {
  return (
    <TableData
      colspan={toNumberOrUndefined(colSpan)}
      rowspan={toNumberOrUndefined(rowSpan)}
      align={align}
    >
      {children}
    </TableData>
  );
};

MdxMdxTableData.displayName = 'MdxTableData';

export {
  MdxMdxTable,
  MdxMdxTableHead,
  MdxMdxTableRow,
  MdxMdxTableHeader,
  MdxMdxTableData,
  MdxStickyTable,
};
